import { sessionApiClient } from "@planningcenter/cc-api-client"
import {
  GivingPerson,
  Organization,
  PaymentMethod,
  Statement,
  Text2GivePhoneNumber,
  RecurringDonationsResponse,
  RecurringDonationResource,
  RecurringDonationDesignationResource,
  RecurringDonationResponse,
  NewRecurringDonationDesignationPayload,
  RecurringDonationDesignationPayload,
  RecurringDonation,
  Designation,
  FundResource,
  PledgesResource,
  PledgeResource,
  PledgeCampaignResource,
  Pledge,
  PledgeCampaign,
  isDesignation,
  isFund,
  isPledgeCampaign,
  isGivingPerson,
} from "./types"
import { Fund, Schedule } from "source/giving/donate/form/types"

export async function fetchGivingPerson(): Promise<GivingPerson> {
  const response = await sessionApiClient.get("/giving/v2/me")
  return response.data.attributes
}

export async function fetchText2GivePhoneNumber(): Promise<Text2GivePhoneNumber | null> {
  const response = await sessionApiClient.get(
    "/giving/v2/me/text_to_give_phone_number",
  )
  if (response.data) {
    const { attributes, relationships } = response.data
    return {
      id: response.data.id,
      phone_number: attributes.phone_number,
      payment_method_id: relationships?.payment_method?.data?.id ?? null,
    }
  } else {
    return null
  }
}

export async function fetchStatements(): Promise<Statement[]> {
  const response = await sessionApiClient.get("/giving/v2/me/statements")
  return response.data.map(
    (item: { id: string; attributes: Omit<Statement, "id"> }) => ({
      id: item.id,
      ...item.attributes,
    }),
  )
}

export async function fetchOrganization(): Promise<Organization> {
  const response = await sessionApiClient.get("/giving/v2")
  return {
    id: response.data.id,
    ...response.data.attributes,
  }
}

export async function fetchFunds(): Promise<Fund[]> {
  const response = await sessionApiClient.get("/giving/v2/funds")
  return response.data.map(
    (item: { id: string; attributes: Omit<Fund, "id"> }) => ({
      id: item.id,
      ...item.attributes,
    }),
  )
}

export async function fetchPaymentMethods(): Promise<PaymentMethod[]> {
  const response = await sessionApiClient.get("/giving/v2/me/payment_methods")
  return response.data.map(
    (item: { id: string; attributes: Omit<PaymentMethod, "id"> }) => ({
      id: item.id,
      ...item.attributes,
    }),
  )
}

export async function fetchRecurringDonations(): Promise<{
  recurringDonations: RecurringDonation[]
}> {
  const response: RecurringDonationsResponse = await sessionApiClient.get(
    "/giving/v2/me/recurring_donations?include=designations,designations.fund",
  )
  return parseRecurringDonationsResponse(response)
}

function parseFunds(
  response: RecurringDonationsResponse | RecurringDonationResponse,
): Fund[] {
  return response.included.filter(isFund).map((item: FundResource) => ({
    id: item.id,
    ...item.attributes,
  }))
}

function parseDesignations(
  response: RecurringDonationsResponse | RecurringDonationResponse,
  funds: Fund[],
): Designation[] {
  return response.included
    .filter(isDesignation)
    .map((item: RecurringDonationDesignationResource) => ({
      id: item.id,
      amount: (item.attributes.amount_cents / 100).toString(),
      fund: funds.find((fund) => fund.id === item.relationships.fund.data.id)!,
    }))
}

function parseRecurringDonationData(
  item: RecurringDonationResource | RecurringDonationResponse["data"],
  designations: Designation[],
): RecurringDonation {
  return {
    id: item.id,
    ...item.attributes,
    payment_method_id: item.relationships.payment_method.data?.id || null,
    designations: item.relationships.designations.data.map((designation) => {
      const includedDesignation = designations.find(
        (d) => d.id === designation.id,
      )!
      return { ...includedDesignation }
    }),
  }
}

function parseRecurringDonationsResponse(response: RecurringDonationsResponse) {
  const funds = parseFunds(response)
  const designations = parseDesignations(response, funds)

  const recurringDonations = response.data.map((item) =>
    parseRecurringDonationData(item, designations),
  )

  return { recurringDonations }
}

export function parseRecurringDonationResponse(
  response: RecurringDonationResponse,
): RecurringDonation {
  const funds = parseFunds(response)
  const designations = parseDesignations(response, funds)

  return parseRecurringDonationData(response.data, designations)
}

export async function fetchPledges(): Promise<{
  pledges: Pledge[]
  pledgeCampaigns: PledgeCampaign[]
  jointGiver: GivingPerson | null
}> {
  const response: PledgesResource = await sessionApiClient.get(
    "/giving/v2/me/pledges?include=joint_giver,pledge_campaign.fund",
  )

  return parsePledgesResponse(response)
}

function parsePledgesResponse(response: PledgesResource) {
  const jointGiver: GivingPerson | null =
    response.included.filter(isGivingPerson)[0]?.attributes || null

  const pledgeFunds: Fund[] = response.included
    .filter(isFund)
    .map((item: FundResource) => ({
      id: item.id,
      ...item.attributes,
    }))

  const pledgeCampaigns: PledgeCampaign[] = response.included
    .filter(isPledgeCampaign)
    .map((item: PledgeCampaignResource) => ({
      id: item.id,
      ...item.attributes,
      fund: pledgeFunds.find(
        (fund) => fund.id === item.relationships.fund.data.id,
      )!,
    }))

  const pledges: Pledge[] = response.data.map((item: PledgeResource) => ({
    id: item.id,
    ...item.attributes,
    joint_giver: jointGiver || null,
    pledge_campaign: pledgeCampaigns.find(
      (campaign) => campaign.id === item.relationships.pledge_campaign.data.id,
    )!,
    fund: pledgeFunds.find(
      (fund) => fund.id === item.relationships.fund.data.id,
    )!,
  }))

  return { pledges, pledgeCampaigns, jointGiver }
}

export async function fetchStartDates(
  schedule: Schedule,
  releaseHoldAt: string | null,
): Promise<string[]> {
  const response = await sessionApiClient.post(
    "/giving/v2/recurring_donation_start_dates",
    { data: { attributes: { schedule, start_date: releaseHoldAt } } },
  )
  return response.data.attributes.occurrences
}

export function includedDesignationsToData(
  designations: Designation[],
): (
  | RecurringDonationDesignationPayload
  | NewRecurringDonationDesignationPayload
)[] {
  return designations.map((designation) => {
    const base: NewRecurringDonationDesignationPayload = {
      type: "RecurringDonationDesignation",
      attributes: { amount: designation.amount },
      relationships: {
        fund: {
          data: {
            type: "Fund",
            id: designation.fund.id,
          },
        },
      },
    }

    return designation.id
      ? ({ ...base, id: designation.id } as RecurringDonationDesignationPayload)
      : base
  })
}
